export const useStrapiUtils = () => {
  /** Populating in the context of the Strapi REST API means
   * including additional content with your response by returning more
   * fields than the ones returned by default. You use the populate parameter to achieve this.
   * https://docs.strapi.io/dev-docs/api/rest/guides/understanding-populate#detailed-population-strategy */

  /** All components allowed in the page dynamic zone (must match
   * prvidrcms/src/api/page/content-types/page/schema.json). Strapi v5 cannot
   * deep-populate nested component media through the shared "*" strategy, so
   * we use the `on` fragment syntax — but `on` EXCLUDES any component not
   * listed, hence every dynamic zone component must appear here. */
  const pageDynamicZoneComponents = [
    "page.content-block",
    "page.page-hero",
    "page.image-text",
    "page.plan-section",
    "page.icon-grid",
    "page.app-banner",
    "page.rich-text",
    "page.cis-documents",
    "page.accordion",
    "page.image-content",
    "page.document-list",
    "page.complaints-handling",
    "page.about-us",
    "page.network-coverage",
    "page.outage-map",
    "page.faq-content",
    "page.simple-text",
    "page.roaming-rates",
    "page.extra-packs",
    "page.hrt-authenticator",
    "page.join-widget",
    "page.page-hero-mobile",
    "page.simple-image",
    "page.free-one-pass-membership",
    "page.translated-documents",
  ];

  const pageFieldsToPopulate = {
    populate: {
      components: {
        on: Object.fromEntries(
          pageDynamicZoneComponents.map((name) => [
            name,
            // translated-documents holds media inside a nested repeatable
            // component, which needs one extra populate level
            name === "page.translated-documents"
              ? { populate: { documents: { populate: "*" } } }
              : { populate: "*" },
          ])
        ),
      },
    },
  };
  const getComponent = (name: string) => {
    const emptyComponent = defineAsyncComponent(
      () => import("@/components/Strapi/Empty.vue")
    );
    const components: Record<string, any> = {
      "page.page-hero": defineAsyncComponent(
        () => import("@/components/Strapi/PageHero.vue")
      ),
      "page.page-hero-mobile": defineAsyncComponent(
        () => import("@/components/Strapi/PageHeroMobile.vue")
      ),
      "page.join-widget": defineAsyncComponent(
        () => import("@/components/Strapi/JoinWidget.vue")
      ),
      "page.icon-grid": defineAsyncComponent(
        () => import("@/components/Strapi/IconGrid.vue")
      ),
      "page.plan-section": defineAsyncComponent(
        () => import("@/components/Strapi/PlanSection.vue")
      ),
      "page.image-text": defineAsyncComponent(
        () => import("@/components/Strapi/ImageText.vue")
      ),
      "page.app-banner": defineAsyncComponent(
        () => import("@/components/Strapi/AppBanner.vue")
      ),
      "page.rich-text": defineAsyncComponent(
        () => import("@/components/Strapi/RichText.vue")
      ),
      "page.cis-documents": defineAsyncComponent(
        () => import("@/components/Strapi/CisDocuments.vue")
      ),
      "page.accordion": defineAsyncComponent(
        () => import("@/components/Strapi/Accordions.vue")
      ),
      "page.image-content": defineAsyncComponent(
        () => import("@/components/Strapi/ImageContent.vue")
      ),
      "page.complaints-handling": defineAsyncComponent(
        () => import("@/components/Strapi/ComplaintsHandling.vue")
      ),
      "page.about-us": defineAsyncComponent(
        () => import("@/components/Strapi/AboutUs.vue")
      ),
      "page.network-coverage": defineAsyncComponent(
        () => import("@/components/Strapi/NetworkCoverage.vue")
      ),
      "page.outage-map": defineAsyncComponent(
        () => import("@/components/Strapi/OutageMap.vue")
      ),
      "page.faq-content": defineAsyncComponent(
        () => import("@/components/Strapi/FAQPage.vue")
      ),
      "page.simple-text": defineAsyncComponent(
        () => import("@/components/Strapi/SimpleText.vue")
      ),
      "page.roaming-rates": defineAsyncComponent(
        () => import("@/components/Strapi/RoamingRates.vue")
      ),
      "page.simple-image": defineAsyncComponent(
        () => import("@/components/Strapi/SimpleImage.vue")
      ),
      // Extra-packs now has a dedicated page at /extra-packs
      // "page.extra-packs": defineAsyncComponent(
      //   () => import("@/components/Strapi/ExtraPacks.vue")
      // ),
      "page.hrt-authenticator": defineAsyncComponent(
        () => import("@/components/Strapi/HrtAuthentication.vue")
      ),
      "page.free-one-pass-membership": defineAsyncComponent(
        () => import("@/components/Strapi/FreeOnePassMembership.vue")
      ),
      "page.translated-documents": defineAsyncComponent(
        () => import("@/components/Strapi/TranslatedDocuments.vue")
      ),
    };
    return components[name] || emptyComponent;
  };

  const getImageData = (image: any) => {
    if (!image || !image) {
      return null;
    }
    return image;
  };

  return { pageFieldsToPopulate, getComponent, getImageData };
};
